import { type MutableRefObject } from 'react';
import { type BookmakersConfig, type OddsFormat, type OddsFormatIs } from '@cx-web/domain-types';

import { type Country, type Repub } from '@cx-modules/constants';
import { type StateCode } from '@cx-modules/types';

export type { OddsFormatIs };

export type CallbackFn = (...args: unknown[]) => void;

export type BetslipProviderProps = {
  /** Bookmaker config (used for clickout tracking display names). Sourced from siteConfig. */
  bookmakers: BookmakersConfig;
  countryCode: Country;
  /**
   * Called with the new open state whenever the betslip opens/closes. Lets a host (e.g. the GZ
   * iframe experience) inject side effects without this provider depending on that host's code.
   */
  onBetslipOpenChange?: (isOpen: boolean) => void;
  persistedBetslipData: {
    betslip?: Bet[];
    bookieOdds?: BookieOdds[];
    selectedBookie?: BookieOdds | undefined;
    stake?: string;
  };
  repub: Repub;
  subdivisionCode: StateCode | '';
  userOddsFormat: OddsFormat;
};

export type BetslipEntry = {
  betId: number;
  odds?: number;
  selectedBookie: string;
};

export type Betslip = Record<string, BetslipEntry>;

export const betslipType = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
};

export type BetslipType = (typeof betslipType)[keyof typeof betslipType];

export type BookieOdds = Pick<
  Odds,
  'betIds' | 'bookmakerCode' | 'bookmakerType' | 'oddsDecimal' | 'oddsFractional' | 'oddsUs'
> & { index?: number };

export type BetslipUtils = {
  calculateBookieOdds: (data: Bet[]) => BookieOdds[];
  calculateBookieOddsAndSelectedBookieOdds: (
    data: Bet[],
    selectedBookieCode?: string,
  ) => {
    bookieOdds: BookieOdds[];
    selectedBookie: BookieOdds | undefined;
  };
  calculateSelectedBookieOdds: (data: Bet[], selectedBookieCode?: string) => BookieOdds | undefined;
  getBestOdds: (bet: any, userOddsFormat: OddsFormat) => string;
  getBestOddsFromBet: (odds: Odds[]) => {
    bestBookie?: string;
    bestOddsDecimal?: number;
    bestOddsFractional?: string;
    bestOddsUs?: number;
  };
  getBetMeta: (bet: any) => {
    betId: number;
    betName: string;
    marketName: string;
    spread: string | number;
    subeventName: string;
  };
  getBetSelectedBookieFromLocalStore: (betId: string) => string | null;
  getBetslipAPI: (
    betIds: number[],
    countryCode: Country,
    state: StateCode | '',
    completion: (success: any, res: any) => void,
  ) => void;
  getBetslipCookieArray: () => [string, string][];
  getLocalStorageBetslip: () => Betslip;
  getOdds: (odds: any, opening: boolean, userOddsFormat: OddsFormat) => string;
  getSelectedBookieBetOdds: (selectedBookie: BookieOdds, userOddsFormat: OddsFormat) => string | number;
  postOddsClick: (betId: number, bookmakerCode: string, countryCode: Country, state: StateCode | '') => void;
  trackBetSlip: (betSlip: TrackBet[], stake?: number) => void;
};

export type BetslipMeta = {
  bookmakers: BookmakersConfig;
  callbacksRef: MutableRefObject<Record<string, CallbackFn[]>>;
  oddsFormatIs: {
    American: boolean;
    Decimal: boolean;
    Fractional: boolean;
  };
  repub: Repub;
  userOddsFormat: OddsFormat;
};

export type Bet = {
  bestOddsBookmakers: string;
  bestOddsDecimal: number;
  bestOddsFractional: string;
  bestOddsUs: number;
  betId: number;
  bettypeId: number;
  categoryGroupId: number;
  categoryGroupName: string;
  categoryId: number;
  disabled?: boolean;
  eventId: number;
  eventName: string;
  genericName: string;
  map: string;
  marketId: number;
  marketName: string;
  name: string;
  odds: Odds[];
  subeventId: number;
  subeventName: string;
  uiOptions?: any;
};

export type TrackBet = Partial<
  Omit<
    Bet,
    | 'odds'
    | 'betTypeId'
    | 'categoryGroupId'
    | 'categoryGroupName'
    | 'categoryId'
    | 'uiOptions'
    | 'eventId'
    | 'eventName'
    | 'subeventId'
    | 'map'
  >
>;

export type Odds = {
  betIds?: number[];
  bookmakerBetId: string;
  bookmakerCode: string;
  bookmakerType: 'traditional' | 'exchange';
  clickoutUrl: string;
  eachWayTerms: string;
  index?: number;
  oddsDecimal: number;
  oddsFractional: string;
  oddsUs: string;
  suspended: boolean;
};

export type BetslipState = {
  added: boolean;
  betIds: number[];
  betslip: Bet[];
  bookieOdds: BookieOdds[];
  /** Opaque reference code shown in the clickout error modal, or null when there's no error. */
  clickoutErrorCode: string | null;
  count: number;
  loading: boolean;
  open: boolean;
  selectedBookie: BookieOdds | null;
  stake: number;
};

export type StateUtils = {
  addOrRemoveFromBetslip: (betArray: any[], matchBetIds?: any[], bookie?: string) => void;
  changeStake: (stake: number) => void;
  /** Clears the clickout error code, dismissing the error modal. */
  dismissClickoutError: () => void;
  fetchBetslipData: (shouldSyncModulesBetslip?: boolean) => void;
  handleClearBetslip: () => void;
  handleClickoutToPlaceBet: (bookieFromBookieSelector?: BookieOdds) => void;
  removeBetFromBetslip: (betId: number, disabled?: boolean, bet?: Bet) => void;
  selectBookie: (idx: number) => void;
  storeBetslipData: (data: Bet[]) => void;
  syncModulesBetslipData: () => void;
  toggleAdded: (val?: boolean) => void;
  toggleBetslip: (val?: boolean) => void;
};

export type BetslipProviderData = BetslipUtils & BetslipMeta & BetslipState & StateUtils;

// TODO: custref, betSport and eventName are not currently
// available so was pass these as undefined in tracking
// pending investigation and resolution under CI-1167

export type BetTrackingDataGeneric = {
  betAccumulator: boolean;
  betOdds: number | string;
  betStake: number;
  bookieCode: string;
  bookieName: string;
  custref?: string;
  onsite: boolean;
};

export type BetTrackingDataBetslipBets = {
  betName: string[];
  betSport?: (number | string)[];
  eventName?: (number | string)[];
  marketName: string[];
  subeventName: string[];
};

export type BetTrackingData = BetTrackingDataGeneric & BetTrackingDataBetslipBets;

export type BetTrackingDataKeys = keyof BetTrackingData;

export type BetTrackingDataValues = BetTrackingData[BetTrackingDataKeys];
